import * as Sentry from '@sentry/browser';
import { sentryConfig } from './SentryConfig';

type ExtraContext = { [key: string]: any };
type Tags = { [key: string]: string };

interface LogErrorOptions {
	tags?: Tags;
	additionalContext?: ExtraContext;
	customMessage?: string;
}

export class SentryLogger {
	private static instance: SentryLogger;
	static sentryInitializationSuccess: boolean;
	static getInstance(): SentryLogger {
		if (!SentryLogger.instance) {
			SentryLogger.instance = new SentryLogger();
			if (sentryConfig.sentrySupport === true) {
				try {
					Sentry.init({
						dsn:
							'https://884620698a0447fab6751656c6d0ca13@o167200.ingest.sentry.io/5557301',
						integrations: [
							new Sentry.Integrations.GlobalHandlers({
								onerror: true,
								onunhandledrejection: true,
							}),
							new Sentry.Integrations.Breadcrumbs({
								console: false,
							}),
						],
						debug: true,
						release: WEBMODULE_SDK_VERSION,
						environment: sentryConfig.environment,
						attachStacktrace: true,
						tracesSampleRate: 1.0,
					});
					Sentry.configureScope((scope) => {
						scope.setTag('process', sentryConfig.processName);
						scope.setTag('branch', sentryConfig.branch);
						scope.setUser({ id: sentryConfig.clientId });
					});
					SentryLogger.sentryInitializationSuccess = true;
				} catch (e) {
					SentryLogger.sentryInitializationSuccess = false;
				}
			}
		}
		return SentryLogger.instance;
	}

	logMessage(message: string): void {
		if (
			SentryLogger.sentryInitializationSuccess === true &&
			sentryConfig.sentrySupport === true
		) {
			Sentry.captureMessage(message);
		}
	}

	logError(exception: any, options: LogErrorOptions = {}): void {
		if (
			SentryLogger.sentryInitializationSuccess === true &&
			sentryConfig.sentrySupport === true
		) {
			Sentry.withScope((scope) => {
				if (options.tags) {
					scope.setTags(options.tags);
				}

				let extras: ExtraContext = {};
				const { customMessage } = options;
				if (!!customMessage) {
					extras.customMessage = customMessage;
				}

				if (options.additionalContext) {
					extras = { ...extras, ...options.additionalContext };
				}
				scope.setExtras(extras);
				scope.setTags({ cwaversion: sentryConfig.version });
				Sentry.captureException(exception);
			});
		}
	}

	/* example addBreadCrumb
	addBreadCrumb({
						category: 'primarytokenservice',
						data: {
							status_code: nativeResult.status,
						},
						type: 'http',
						message: 'token service fetch',
						level: 'Debug',
						event_id: 'sample',
						timestamp: 1234
					});
	 */
	addBreadCrumb(breadcrumb: Sentry.Breadcrumb): void {
		if (
			SentryLogger.sentryInitializationSuccess === true &&
			sentryConfig.sentrySupport === true
		) {
			Sentry.addBreadcrumb(breadcrumb);
		}
	}
}
